import Chart from 'react-apexcharts';
import currency from 'currency.js';

import theme from 'styles/theme';
import ChartLegend from '../ChartLegend';
import { percent } from 'v3/utils/formatNumber';
import * as S from './styles';

export type LineChartProps = {
  savingsList: Array<{ mCostOfCredit: number; costOfCredit: number }>;
  nMonths: number;
  interval?: number;
  totalSavings: number;
  apr: number;
  eApr: number;
  monthlyPayment: number;
  mMonthlyPayment: number;
};

const SC_COLOR = theme.colors.primary60;
const TF_COLOR = theme.colors.statusDanger30;

const getMonthLabels = (
  initialMonth: number,
  finalMonth: number,
  interval: number,
  accum: number[] = []
): string[] => {
  if (initialMonth === finalMonth) return accum.map((month) => `${month}mo`);
  return getMonthLabels(
    initialMonth + interval,
    finalMonth,
    interval,
    accum.concat(initialMonth + interval)
  );
};

const LineChart = ({
  savingsList,
  nMonths,
  interval = nMonths / 6,
  monthlyPayment,
  mMonthlyPayment,
  apr,
  eApr,
  totalSavings,
}: LineChartProps) => {
  const arrTraditionalValues = [
    0,
    ...savingsList.map(({ mCostOfCredit }) => Number(mCostOfCredit.toFixed(2))),
  ];

  const arrSavingsClubValues = [
    0,
    ...savingsList.map(({ costOfCredit }) => Number(costOfCredit.toFixed(2))),
  ];

  const categoriesVal = [
    '0mo',
    ...getMonthLabels(0, Number(nMonths), interval),
  ];

  const options: ApexCharts.ApexOptions = {
    chart: {
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    title: {
      align: 'center',
    },
    dataLabels: {
      enabled: false,
    },
    colors: [TF_COLOR, SC_COLOR],
    grid: {
      row: {
        colors: [theme.colors.neutralSilver5], // takes an array which will be repeated on columns
        opacity: 0.5,
      },
    },
    xaxis: {
      categories: categoriesVal,
    },
    yaxis: {
      labels: {
        style: {
          fontWeight: 400,
          fontSize: '12px',
          fontFamily: 'Roboto',
        },
        formatter: (value: number) => {
          return ` ${value / 1000}k`;
        },
      },
    },
    legend: {
      show: false,
    },
    tooltip: {
      custom: ({ dataPointIndex, w }: any) => {
        const data = w.globals.initialSeries;
        const month = w.globals.categoryLabels[dataPointIndex]
          .split(' ')[0]
          .replace('mo', '') as string;
        const loanColor = w.globals.colors as string[];

        return `
        <dl class="tooltip-chart">
        <dt><strong>Month ${month}</strong></dt>
        <dd class="tooltip-chart-item"><p>${
          data[0].name as string
        }</p> <span style="color: ${loanColor[0]};"> ${currency(
          data[0].data[dataPointIndex]
        ).format()}</span></dd>
        <dd class="tooltip-chart-item"><p>${
          data[1].name as string
        }</p> <span class="sc" style="color: ${loanColor[1]};"> ${currency(
          data[1].data[dataPointIndex]
        ).format()}</span></dd>
        </dl>
        `;
      },
    },
  };

  const series = [
    {
      name: 'Traditional Fin.',
      data: arrTraditionalValues,
    },
    {
      name: 'Savings Club',
      data: arrSavingsClubValues,
    },
  ];

  return (
    <S.Wrapper>
      <S.MobileOnly>
        <S.LegendWrapper>
          <ChartLegend
            title={series[0].name}
            firstText={currency(mMonthlyPayment).format() + '/mo'}
            secondText={percent(apr) + ' APR'}
            tooltip="High compounding interest rates cost thousands after it’s all said and done."
            color={TF_COLOR}
          />
          <ChartLegend
            title={series[1].name}
            firstText={currency(monthlyPayment).format() + '/mo'}
            secondText={percent(eApr) + ' EAPR'}
            tooltip="Only pay for your vehicle and administrative fees. No compounding interest, ever."
            color={SC_COLOR}
          />
        </S.LegendWrapper>
      </S.MobileOnly>

      <S.ChartWrapper>
        <Chart options={options} height={330} series={series} type="line" />
      </S.ChartWrapper>

      <S.MobileOnly>
        <S.TotalSavingsContainer>
          <S.TotalSavings>
            Save {currency(totalSavings).format()}
          </S.TotalSavings>
        </S.TotalSavingsContainer>
      </S.MobileOnly>

      <S.DesktopOnly>
        <S.LegendWrapper>
          <ChartLegend
            title={series[0].name}
            firstText={currency(mMonthlyPayment).format() + '/mo'}
            secondText={percent(apr) + 'APR'}
            tooltip="High compounding interest rates cost thousands after it’s all said and done."
            color={TF_COLOR}
          />
          <ChartLegend
            title={series[1].name}
            firstText={currency(monthlyPayment).format() + '/mo'}
            secondText={percent(eApr) + 'EAPR'}
            tooltip="Only pay for your vehicle and administrative fees. No compounding interest, ever."
            color={SC_COLOR}
          />
          <S.TotalSavings>
            Save {currency(totalSavings).format()}
          </S.TotalSavings>
        </S.LegendWrapper>
      </S.DesktopOnly>
    </S.Wrapper>
  );
};

export default LineChart;
